import { i18n } from '../i18n.js';
/**
 * Cloud-mutation guard: dedupes concurrent calls to the same Convex mutation
 * keyed by a caller-supplied name, and surfaces rate-limit failures as a
 * standard .game-notification toast so every screen behaves the same way.
 */
let MoonfallCloudGuard;

(function () {
  if (typeof window === 'undefined') return;
  if (MoonfallCloudGuard) return;

  var RATE_LIMIT_PATTERN = /too many submissions|rate.?limit|429|too many requests|slow down/i;
  var TOAST_FADE_MS = 2400;
  var TOAST_REMOVE_MS = 3000;
  var inflight = new Map();

  function uiText(key, fallback, replacements) {
    try {
      if (i18n && typeof i18n.t === 'function') {
        var translated = i18n.t(key, replacements || {});
        if (translated && translated !== key) return translated;
      }
    } catch (_) {}
    return fallback;
  }

  function isRateLimitError(err) {
    if (!err) return false;
    var message = err && err.message ? String(err.message) : String(err);
    return RATE_LIMIT_PATTERN.test(message);
  }

  function showRateLimitToast(retryAfterMs) {
    try {
      var existing = document.querySelector('.game-notification.cloud-guard-toast');
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

      var seconds = Math.max(1, Math.round((Number(retryAfterMs) || 0) / 1000));
      var text = retryAfterMs && seconds > 0
        ? uiText('common.tryAgainInSeconds', 'Try again in ' + seconds + 's', { seconds: seconds })
        : uiText('common.rateLimited', 'Slow down — try again in a moment.');

      var toast = document.createElement('div');
      toast.className = 'game-notification cloud-guard-toast';
      toast.setAttribute('role', 'status');
      toast.textContent = text;
      document.body.appendChild(toast);

      setTimeout(function () { try { toast.classList.add('fade-out'); } catch (_) {} }, TOAST_FADE_MS);
      setTimeout(function () {
        if (toast.parentNode) toast.parentNode.removeChild(toast);
      }, TOAST_REMOVE_MS);
    } catch (_) {
      // never block the rest of the app on toast rendering
    }
  }

  function decorateRateLimitError(err) {
    var wrapped = err instanceof Error ? err : new Error(String(err && err.message ? err.message : err));
    wrapped.code = 'rate_limited';
    wrapped.original = err;
    return wrapped;
  }

  function run(name, asyncFn) {
    if (typeof name !== 'string' || name.length === 0) {
      return Promise.reject(new Error('MoonfallCloudGuard.run: name is required'));
    }
    if (typeof asyncFn !== 'function') {
      return Promise.reject(new Error('MoonfallCloudGuard.run: asyncFn must be a function'));
    }
    if (inflight.has(name)) {
      return inflight.get(name);
    }

    var pending;
    try {
      var raw = asyncFn();
      pending = Promise.resolve(raw).catch(function (err) {
        if (isRateLimitError(err)) {
          showRateLimitToast(0);
          throw decorateRateLimitError(err);
        }
        throw err;
      });
    } catch (err) {
      return Promise.reject(err);
    }

    inflight.set(name, pending);
    var clearSlot = function () { inflight.delete(name); };
    pending.then(clearSlot, clearSlot);
    return pending;
  }

  MoonfallCloudGuard = {
    run: run,
    showRateLimitToast: showRateLimitToast,
    isRateLimitError: isRateLimitError,
    _inflightSize: function () { return inflight.size; },
  };
})();

export { MoonfallCloudGuard };
